/**
 * MPP - Auto Trendlines — reverse-engineered CUE Elite "Memory" (TradeStation port).
 *
 * Default Auto Trendlines engine (also drives the scanner TL criteria).
 * Same walk as `docs/MPP-CueMemory-EasyLanguage.txt` / `final_model.py`:
 *   nine Fibonacci pivot classes × two sides, one current line per machine,
 *   parent hop on direction failure, ancestry survives a validity veto,
 *   remember a superseded line only if it already broke while current.
 *
 * Prices are cent-rounded (TradeStation's adjusted grid). Warmup is counted
 * from the LEFT of the loaded window (EasyLanguage CurrentBar). Live segments
 * are origin-pivot → destination-pivot (the two connected wicks). When
 * `extendRight` is on they ray past the last bar (`TL_SetExtRight`); off
 * they stop on the last candle.
 */
import type { Bar } from "@/lib/chart/indicatorTypes";

export const CUE_MEMORY_CLASSES = [3, 5, 8, 13, 21, 34, 55, 89, 144] as const;

const EPS = 0.000001;
const MAX_HIST = 4000;
const MAX_OBJECTS = 2500;

export type CueMemoryParams = {
  warmupBars?: unknown;
  showHistory?: unknown;
  showLive?: unknown;
  lookback?: unknown;
  /** Ray past the last candle (TradeStation ExtRight). Default on. */
  extendRight?: unknown;
};

export type CueMemorySeg = {
  t1: number;
  p1: number;
  t2: number;
  p2: number;
  kind: "support" | "resistance";
  role: "live" | "history";
  /** Live lines are orig→dest pivots; the overlay/TV ray extends right. */
  extendRight?: boolean;
};

const num = (v: unknown, d: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const flag = (v: unknown, d: boolean) =>
  v === undefined || v === null ? d : !(v === false || v === 0 || v === "false" || v === "0" || v === "off");

const cents = (x: number) => Math.round(x * 100) / 100;

export function lineVal(oa: number, op: number, da: number, dp: number, x: number): number {
  if (da === oa) return op;
  return op + ((dp - op) * (x - oa)) / (da - oa);
}

function pivOk(side: 0 | 1, pb: number, cnt: number, H: number[], L: number[]): boolean {
  if (pb - cnt < 0 || pb + cnt >= H.length) return false;
  for (let k = 1; k <= cnt; k++) {
    if (side === 0) {
      if (L[pb]! > L[pb - k]! || L[pb]! >= L[pb + k]!) return false;
    } else if (H[pb]! < H[pb - k]! || H[pb]! <= H[pb + k]!) {
      return false;
    }
  }
  return true;
}

function dirOk(side: 0 | 1, a: number, q: number, H: number[], L: number[]): boolean {
  return side === 0 ? L[a]! < L[q]! : H[a]! > H[q]!;
}

function validOk(side: 0 | 1, a: number, q: number, H: number[], L: number[], Cl: number[]): boolean {
  const op = side === 0 ? L[a]! : H[a]!;
  const dp = side === 0 ? L[q]! : H[q]!;
  for (let x = a + 1; x <= q - 1; x++) {
    const lnv = lineVal(a, op, q, dp, x);
    if (side === 0 && Cl[x]! < lnv - EPS) return false;
    if (side === 1 && Cl[x]! > lnv + EPS) return false;
  }
  return true;
}

export type CueMemoryMachine = {
  pvBar: number;
  pvPar: number;
  orig: number;
  dest: number;
  drawB: number;
  brkB: number;
  brkP: number;
};

export type CueMemoryHist = {
  ob: number;
  op: number;
  eb: number;
  ep: number;
  side: 0 | 1;
};

function emptyMachine(): CueMemoryMachine {
  return { pvBar: -1, pvPar: -1, orig: -1, dest: -1, drawB: -1, brkB: -1, brkP: 0 };
}

/** One Memory pass. `onBar` sees machines AFTER this bar's breaks and new
 *  draws — the scanner setups classify that snapshot. */
export function walkCueMemory(
  H: number[],
  L: number[],
  Cl: number[],
  warmupBars: number,
  onBar?: (i: number, machines: readonly CueMemoryMachine[]) => void,
): { machines: CueMemoryMachine[]; hist: CueMemoryHist[] } {
  const n = H.length;
  const wp = (side: 0 | 1, i: number) => (side === 0 ? L[i]! : H[i]!);
  const machines: CueMemoryMachine[] = Array.from({ length: 18 }, emptyMachine);
  const hist: CueMemoryHist[] = [];

  const keepIfRemembered = (mm: number, currentBar1: number) => {
    const m = machines[mm]!;
    if (m.orig < 0) return;
    if (m.drawB + 1 < warmupBars) return;
    if (m.brkB < 0 || m.brkB + 1 >= currentBar1) return;
    const side = (mm % 2) as 0 | 1;
    const op = wp(side, m.orig);
    for (let j = 0; j < hist.length; j++) {
      const h = hist[j]!;
      if (h.ob === m.orig && h.eb === m.brkB && Math.abs(h.op - op) < 0.005) return;
    }
    if (hist.length >= MAX_HIST) return;
    hist.push({ ob: m.orig, op, eb: m.brkB, ep: m.brkP, side });
  };

  for (let i = 0; i < n; i++) {
    const bi = i + 1;

    for (let mm = 0; mm < 18; mm++) {
      const m = machines[mm]!;
      if (m.orig >= 0 && m.brkB < 0 && bi > m.drawB + 1) {
        const side = (mm % 2) as 0 | 1;
        const vLine = lineVal(m.orig, wp(side, m.orig), m.dest, wp(side, m.dest), i);
        if ((side === 0 && Cl[i]! < vLine - EPS) || (side === 1 && Cl[i]! > vLine + EPS)) {
          m.brkB = i;
          m.brkP = vLine;
        }
      }
    }

    for (let ci = 0; ci < 9; ci++) {
      const cc = CUE_MEMORY_CLASSES[ci]!;
      const cb1 = bi - (cc + 1);
      if (cb1 - cc < 1) continue;
      const cb = cb1 - 1;
      for (let sd = 0; sd <= 1; sd++) {
        const mm = ci * 2 + sd;
        const side = sd as 0 | 1;
        if (!pivOk(side, cb, cc, H, L)) continue;
        const m = machines[mm]!;
        const prevB = m.pvBar;
        let drawnB = -1;
        let linkB = -1;
        if (prevB >= 0) {
          let candB = prevB;
          if (!dirOk(side, candB, cb, H, L)) candB = m.pvPar;
          if (candB >= 0 && dirOk(side, candB, cb, H, L)) {
            linkB = candB;
            if (validOk(side, candB, cb, H, L, Cl)) drawnB = candB;
          }
        }
        if (drawnB >= 0) {
          keepIfRemembered(mm, bi);
          m.orig = drawnB;
          m.dest = cb;
          m.drawB = i;
          m.brkB = -1;
          m.brkP = 0;
        }
        m.pvBar = cb;
        m.pvPar = linkB;
      }
    }
    onBar?.(i, machines);
  }
  return { machines, hist };
}

export function computeCueMemory(bars: Bar[], p: CueMemoryParams = {}): CueMemorySeg[] {
  const warmupBars = Math.max(1, Math.round(num(p.warmupBars, 300)));
  const showHistory = flag(p.showHistory, true);
  const showLive = flag(p.showLive, true);
  const extendRight = flag(p.extendRight, true);
  const lookback = Math.max(0, Math.round(num(p.lookback, 5000)));
  const src = Array.isArray(bars) ? bars : [];
  const start = lookback > 0 && src.length > lookback ? src.length - lookback : 0;
  const win = src.slice(start);
  const n = win.length;
  if (n < 20) return [];

  const H = new Array<number>(n);
  const L = new Array<number>(n);
  const Cl = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    H[i] = cents(win[i]!.high);
    L[i] = cents(win[i]!.low);
    Cl[i] = cents(win[i]!.close);
  }

  const wp = (side: 0 | 1, i: number) => (side === 0 ? L[i]! : H[i]!);
  const { machines, hist } = walkCueMemory(H, L, Cl, warmupBars);

  const out: CueMemorySeg[] = [];
  const push = (seg: CueMemorySeg) => {
    if (out.length >= MAX_OBJECTS) return;
    out.push(seg);
  };

  if (showHistory) {
    for (const h of hist) {
      push({
        t1: win[h.ob]!.t,
        p1: h.op,
        t2: win[h.eb]!.t,
        p2: h.ep,
        kind: h.side === 0 ? "support" : "resistance",
        role: "history",
      });
    }
  }

  const lastI = n - 1;
  for (let mm = 0; mm < 18; mm++) {
    const m = machines[mm]!;
    if (m.orig < 0 || m.drawB + 1 < warmupBars) continue;
    const side = (mm % 2) as 0 | 1;
    const kind = side === 0 ? "support" : "resistance";
    if (m.brkB >= 0) {
      if (!showHistory) continue;
      let dup = false;
      for (const h of hist) {
        if (h.ob === m.orig && h.eb === m.brkB) {
          dup = true;
          break;
        }
      }
      if (dup) continue;
      push({
        t1: win[m.orig]!.t,
        p1: wp(side, m.orig),
        t2: win[m.brkB]!.t,
        p2: m.brkP,
        kind,
        role: "history",
      });
      continue;
    }
    if (!showLive) continue;
    let dup = false;
    for (let k = 0; k < mm; k++) {
      const o = machines[k]!;
      if (o.orig === m.orig && o.dest === m.dest && o.brkB < 0 && k % 2 === mm % 2) {
        dup = true;
        break;
      }
    }
    if (dup) continue;
    const p1 = wp(side, m.orig);
    const pDest = wp(side, m.dest);
    // Always draw orig → dest so the second wick is the visible anchor.
    // ExtRight (default) rays past the last bar; off continues only to it.
    push({
      t1: win[m.orig]!.t,
      p1,
      t2: win[m.dest]!.t,
      p2: pDest,
      kind,
      role: "live",
      extendRight,
    });
    if (!extendRight && m.dest < lastI) {
      push({
        t1: win[m.dest]!.t,
        p1: pDest,
        t2: win[lastI]!.t,
        p2: lineVal(m.orig, p1, m.dest, pDest, lastI),
        kind,
        role: "live",
      });
    }
  }

  return out;
}
